import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

function StoryDetailPage() {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStoryDetail();
    fetchChapters();
  }, [id]);

  const fetchStoryDetail = async () => {
    try {
      const response = await fetch(`/api/stories/${id}`);
      const data = await response.json();
      if (data.story) setStory(data.story);
    } catch (error) {
      console.error('获取故事详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    try {
      const response = await fetch(`/api/stories/${id}/chapters?limit=100`);
      const data = await response.json();
      if (data.chapters) {
        setChapters(data.chapters);
        // 提取贡献者列表
        const uniqueContributors = new Map();
        data.chapters.forEach(chapter => {
          if (chapter.author_id && !uniqueContributors.has(chapter.author_id)) {
            uniqueContributors.set(chapter.author_id, {
              id: chapter.author_id,
              username: chapter.author_name || '匿名',
              chapterCount: 1
            });
          } else if (chapter.author_id) {
            const c = uniqueContributors.get(chapter.author_id);
            c.chapterCount++;
          }
        });
        setContributors(Array.from(uniqueContributors.values()));
      }
    } catch (error) {
      console.error('获取章节列表失败:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">加载中...</div>;
  }

  if (!story) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❌</div>
        <h2 className="text-2xl font-bold">故事不存在</h2>
        <Link to="/stories" className="text-primary-400 mt-4 inline-block">← 返回故事列表</Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* 左侧：故事信息 */}
      <div className="lg:col-span-1 space-y-4">
        {/* 故事卡片 */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 sticky top-4">
          <h1 className="text-xl font-bold mb-2">{story.title}</h1>
          <p className="text-gray-400 text-sm mb-4">{story.description}</p>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">📖 阅读</span>
              <span>{story.views || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">❤️ 喜欢</span>
              <span>{story.likes || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">📝 章节</span>
              <span>{chapters.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">👥 贡献者</span>
              <span>{contributors.length}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-700">
            <span className="text-xs text-gray-500">分类</span>
            <div className="mt-1">
              <span className="px-2 py-1 bg-gray-700 rounded text-xs">
                {story.category || '其他'}
              </span>
            </div>
          </div>

          <Link
            to={`/relay/${id}`}
            className="mt-4 block w-full text-center py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 rounded-lg font-bold"
          >
            ✍️ 立即接龙
          </Link>
        </div>

        {/* 贡献者列表 */}
        {contributors.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="font-bold mb-3">👥 贡献者</h3>
            <div className="space-y-2">
              {contributors.map(c => (
                <div key={c.id} className="flex justify-between items-center text-sm">
                  <span>{c.username}</span>
                  <span className="text-gray-500">{c.chapterCount} 章</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 中间：章节列表 */}
      <div className="lg:col-span-3">
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
            <h2 className="font-bold text-lg">📚 章节列表 ({chapters.length}章)</h2>
            <span className="text-sm text-gray-400">按时间排序</span>
          </div>

          {chapters.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <div className="text-6xl mb-4">📝</div>
              <p className="text-lg mb-4">还没有章节，成为第一个创作者！</p>
              <Link
                to={`/relay/${id}`}
                className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-bold"
              >
                写下第一章
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {chapters.map((chapter, index) => (
                <div
                  key={chapter.id}
                  className="p-4 hover:bg-gray-700 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="px-2 py-1 bg-primary-900 text-primary-300 rounded text-xs font-bold">
                          第{chapter.chapter_number}章
                        </span>
                        <span className="text-gray-500 text-sm">
                          by {chapter.author_name || '匿名'}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-2">{chapter.title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-3 mb-3">
                        {chapter.content?.substring(0, 200) || '无内容'}...
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>📅 {new Date(chapter.created_at).toLocaleDateString('zh-CN')}</span>
                        <span>❤️ {chapter.likes || 0}</span>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2 ml-4">
                      <Link
                        to={`/chapter/${chapter.id}`}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded text-sm font-medium"
                      >
                        阅读
                      </Link>
                      <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm">
                        ❤️ {chapter.likes || 0}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 接龙规则 */}
        <div className="mt-6 bg-gradient-to-r from-purple-900 to-blue-900 rounded-lg p-6 border border-purple-700">
          <h3 className="font-bold text-lg mb-4">📜 接龙规则</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start space-x-2">
              <span className="text-2xl">1️⃣</span>
              <div>
                <h4 className="font-bold mb-1">延续剧情</h4>
                <p className="text-gray-400">基于前一章内容继续创作，保持故事连贯性</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-2xl">2️⃣</span>
              <div>
                <h4 className="font-bold mb-1">尊重设定</h4>
                <p className="text-gray-400">不随意更改已有的人物设定和世界观</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-2xl">3️⃣</span>
              <div>
                <h4 className="font-bold mb-1">创意无限</h4>
                <p className="text-gray-400">发挥创意，让故事更加精彩有趣</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StoryDetailPage;
